import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAdminOrdersChart, getAdminRedemptionsSummary, AdminTopRedeemer } from "../../../api/api-hook";

type ChartPoint = { date: string; label: string; count: number };

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

// Redeemed At in Eastern time, as HubSpot shows it. Redemptions imported from the old system only have a
// date, stored as exactly noon Eastern, so those show the date alone.
const formatRedeemedAt = (iso: string) => {
  const d = new Date(iso);
  if (!iso || isNaN(d.getTime())) return "—";
  const dateOnly =
    d.getUTCMilliseconds() === 0 &&
    d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hourCycle: "h23" }) === "12:00:00";
  return d.toLocaleString(undefined, {
    timeZone: "America/New_York",
    month: "short",
    day: "2-digit",
    year: "numeric",
    ...(dateOnly ? {} : { hour: "2-digit", minute: "2-digit" }),
  });
};

// ── Date range (Eastern dates, YYYY-MM-DD) ──
const easternToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const addDays = (iso: string, n: number) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const shortDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

type Preset = "7" | "30" | "90" | "year" | "custom";
const PRESETS: { key: Exclude<Preset, "custom">; label: string; title: string }[] = [
  { key: "7", label: "7D", title: "Last 7 days" },
  { key: "30", label: "30D", title: "Last 30 days" },
  { key: "90", label: "90D", title: "Last 90 days" },
  { key: "year", label: "YTD", title: "This year" },
];
const presetRange = (key: Exclude<Preset, "custom">) => {
  const to = easternToday();
  return { from: key === "year" ? `${to.slice(0, 4)}-01-01` : addDays(to, -(Number(key) - 1)), to };
};

const RangePicker = ({
  preset, from, to, onPreset, onDates,
}: {
  preset: Preset; from: string; to: string;
  onPreset: (p: Exclude<Preset, "custom">) => void; onDates: (from: string, to: string) => void;
}) => {
  const today = easternToday();
  return (
    <div className="d-flex flex-wrap align-items-center gap-2" style={{ minWidth: 0, maxWidth: "100%" }}>
      <div className="btn-group btn-group-sm" role="group" aria-label="Date range">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            title={p.title}
            className="btn"
            onClick={() => onPreset(p.key)}
            style={preset === p.key
              ? { backgroundColor: BRAND_GREEN, borderColor: BRAND_GREEN, color: "#fff" }
              : { backgroundColor: "#fff", borderColor: "#ced4da", color: "#495057" }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="d-flex align-items-center gap-1" style={{ flex: "1 1 240px", minWidth: 0, maxWidth: 300 }}>
        <input
          type="date"
          aria-label="From date"
          className="form-control form-control-sm"
          style={{ flex: "1 1 0", minWidth: 0 }}
          value={from}
          max={to}
          onChange={(e) => e.target.value && onDates(e.target.value, to)}
        />
        <span className="text-muted small">–</span>
        <input
          type="date"
          aria-label="To date"
          className="form-control form-control-sm"
          style={{ flex: "1 1 0", minWidth: 0 }}
          value={to}
          min={from}
          max={today}
          onChange={(e) => e.target.value && onDates(from, e.target.value)}
        />
      </div>
    </div>
  );
};

const SummaryCard = ({ label, sub, value, accent }: { label: string; sub?: string; value: string | number; accent: string }) => (
  <div className="col-12 col-sm-6 col-lg-3">
    <div className="border rounded p-3 h-100" style={{ borderLeft: `4px solid ${accent}` }}>
      <div className="text-muted small text-uppercase fw-semibold">{label}</div>
      {sub && <div className="text-muted mb-1" style={{ fontSize: "0.72rem" }}>{sub}</div>}
      <div className="fw-bold" style={{ fontSize: "1.6rem", color: BRAND_GREEN, lineHeight: 1.2 }}>{value}</div>
    </div>
  </div>
);

const DailyChart = ({
  title, note, data, loading, error, color, unit,
}: {
  title: string; note: string; data: ChartPoint[]; loading: boolean; error: string | null; color: string; unit: string;
}) => (
  <div className="border rounded p-3 p-md-4 h-100">
    <div className="d-flex align-items-start justify-content-between flex-wrap gap-1 mb-3">
      <div>
        <h6 className="fw-semibold mb-0" style={{ color: BRAND_GREEN }}>{title}</h6>
        <div className="text-muted" style={{ fontSize: "0.75rem" }}>{note}</div>
      </div>
      {!loading && !error && data.length > 0 && (
        <span className="text-muted small">{`${data[0].label} – ${data[data.length - 1].label}`}</span>
      )}
    </div>

    {loading && (
      <div className="d-flex align-items-center justify-content-center" style={{ height: 240 }}>
        <div className="spinner-border spinner-border-sm me-2" style={{ color: BRAND_GREEN }} role="status" />
        <span className="text-muted small">Loading chart…</span>
      </div>
    )}

    {error && !loading && <div className="alert alert-danger small py-2">{error}</div>}

    {!loading && !error && (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "#6c757d" }}
            interval="preserveStartEnd"
            minTickGap={14}
            tickLine={false}
            axisLine={{ stroke: "#dee2e6" }}
          />
          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6c757d" }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ fontSize: "0.8rem", borderRadius: 6, border: "1px solid #dee2e6" }}
            formatter={(value) => [value, unit]}
            labelStyle={{ fontWeight: 600, color: BRAND_GREEN }}
          />
          <Bar dataKey="count" fill={color} radius={[3, 3, 0, 0]} maxBarSize={32} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </div>
);

const AdminDashboard = () => {
  const [chartData, setChartData]       = useState<ChartPoint[]>([]);
  const [packageCount, setPackageCount] = useState<number>(0);
  const [refundCount, setRefundCount]   = useState<number>(0);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);

  const [redChart, setRedChart]         = useState<ChartPoint[]>([]);
  const [redTotal, setRedTotal]         = useState<number>(0);
  const [redYear, setRedYear]           = useState<string>("");
  const [redYearTotal, setRedYearTotal] = useState<number>(0);
  const [topRedeemers, setTopRedeemers] = useState<AdminTopRedeemer[]>([]);
  const [redLoading, setRedLoading]     = useState(true);
  const [redError, setRedError]         = useState<string | null>(null);

  const [preset, setPreset] = useState<Preset>("30");
  const [range, setRange]   = useState(() => presetRange("30"));
  const choosePreset = (p: Exclude<Preset, "custom">) => { setPreset(p); setRange(presetRange(p)); };
  const chooseDates = (from: string, to: string) => {
    if (!from || !to) return;
    setPreset("custom");
    setRange(from <= to ? { from, to } : { from: to, to: from });
  };
  const rangeText = preset === "custom"
    ? `${shortDate(range.from)} – ${shortDate(range.to)}`
    : PRESETS.find((p) => p.key === preset)!.title;

  useEffect(() => {
    // A newer range replaces this one; its late answers are ignored
    let current = true;

    setLoading(true);
    setError(null);
    getAdminOrdersChart(range)
      .then((r) => {
        if (!current) return;
        if (r.success) {
          setChartData(r.chartData);
          setPackageCount(r.packageCount ?? 0);
          setRefundCount(r.refundCount ?? 0);
        } else setError("Failed to load chart data.");
      })
      .catch(() => current && setError("Network error loading chart."))
      .finally(() => current && setLoading(false));

    // Loaded separately: the first load after a deploy reads every redemption and can take a little longer
    setRedLoading(true);
    setRedError(null);
    getAdminRedemptionsSummary(range)
      .then((r) => {
        if (!current) return;
        if (r.success) {
          setRedChart(r.chartData);
          setRedTotal(r.total ?? 0);
          setRedYear(r.year);
          setRedYearTotal(r.yearTotal ?? 0);
          setTopRedeemers(r.topRedeemers || []);
        } else setRedError("Failed to load redemptions.");
      })
      .catch(() => current && setRedError("Network error loading redemptions."))
      .finally(() => current && setRedLoading(false));

    return () => { current = false; };
  }, [range]);

  const peakDay = chartData.reduce((best, d) => (d.count > best.count ? d : best), { date: "", label: "", count: 0 });

  return (
    <div>
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-4">
        <div>
          <h4 className="mb-1" style={{ color: BRAND_GREEN }}>Dashboard</h4>
          <p className="text-muted small mb-0">Welcome to the VSGA Admin Portal.</p>
        </div>
        <RangePicker preset={preset} from={range.from} to={range.to} onPreset={choosePreset} onDates={chooseDates} />
      </div>

      {/* Summary cards */}
      <div className="row g-3 mb-4">
        <SummaryCard label="Packages purchased" sub={rangeText} value={loading ? "—" : packageCount} accent={BRAND_GREEN} />
        <SummaryCard
          label="Peak order day"
          sub={rangeText}
          value={loading ? "—" : peakDay.count > 0 ? `${peakDay.count} on ${peakDay.label}` : "—"}
          accent={BRAND_GOLD}
        />
        <SummaryCard label="Refunds" sub={rangeText} value={loading ? "—" : refundCount} accent="#dc3545" />
        <SummaryCard label="Redemptions" sub={rangeText} value={redLoading ? "—" : redTotal.toLocaleString()} accent="#146c43" />
      </div>

      {/* Charts */}
      <div className="row g-3 mb-4">
        <div className="col-xl-6">
          <DailyChart
            title="New Orders"
            note="Purchased orders only; refunded orders are not included"
            data={chartData}
            loading={loading}
            error={error}
            color={BRAND_GREEN}
            unit="Orders"
          />
        </div>
        <div className="col-xl-6">
          <DailyChart
            title="Redemptions"
            note="Offers redeemed per day; reversed redemptions are not included"
            data={redChart}
            loading={redLoading}
            error={redError}
            color={BRAND_GOLD}
            unit="Redemptions"
          />
        </div>
      </div>

      {/* Top redeemers */}
      <div className="border rounded p-3 p-md-4">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-1 mb-3">
          <div>
            <h6 className="fw-semibold mb-0" style={{ color: BRAND_GREEN }}>
              Top 10 Redeemers{redYear ? ` — ${redYear}` : ""}
            </h6>
            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
              Golfers with the most offers redeemed this calendar year
            </div>
          </div>
          {!redLoading && !redError && (
            <span className="text-muted small">{redYearTotal.toLocaleString()} redemptions in {redYear}</span>
          )}
        </div>

        {redLoading && (
          <div className="d-flex align-items-center justify-content-center py-4">
            <div className="spinner-border spinner-border-sm me-2" style={{ color: BRAND_GREEN }} role="status" />
            <span className="text-muted small">Loading redeemers…</span>
          </div>
        )}
        {redError && !redLoading && <div className="alert alert-danger small py-2 mb-0">{redError}</div>}
        {!redLoading && !redError && topRedeemers.length === 0 && (
          <div className="text-muted small">No redemptions yet this year.</div>
        )}

        {!redLoading && !redError && topRedeemers.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.85rem" }}>
              <thead>
                <tr className="text-muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  <th className="fw-semibold" style={{ width: 36 }}>#</th>
                  <th className="fw-semibold">Golfer</th>
                  <th className="fw-semibold d-none d-md-table-cell">Email</th>
                  <th className="fw-semibold">VIP Card #</th>
                  <th className="fw-semibold text-end">Redemptions</th>
                  <th className="fw-semibold d-none d-sm-table-cell">Last Redeemed</th>
                  <th style={{ width: 32 }} />
                </tr>
              </thead>
              <tbody>
                {topRedeemers.map((g, i) => {
                  const name = `${g.firstName} ${g.lastName}`.trim();
                  return (
                    <tr key={g.contactId || g.vipCardNumber || i}>
                      <td className="text-muted">{i + 1}</td>
                      <td className="fw-semibold" style={{ color: BRAND_GREEN }}>{name || "—"}</td>
                      <td className="d-none d-md-table-cell text-muted" style={{ wordBreak: "break-all" }}>{g.email || "—"}</td>
                      <td><span style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{g.vipCardNumber || "—"}</span></td>
                      <td className="text-end">
                        <span
                          style={{
                            display: "inline-block", padding: "0.15rem 0.6rem", borderRadius: 20, fontSize: "0.75rem",
                            fontWeight: 600, backgroundColor: "rgba(25,135,84,0.1)", color: "#146c43",
                          }}
                        >
                          {g.count}
                        </span>
                      </td>
                      <td className="d-none d-sm-table-cell text-nowrap text-muted">{formatRedeemedAt(g.lastRedeemedAt)}</td>
                      <td className="text-center px-0">
                        {g.contactId && (
                          <button
                            type="button"
                            className="btn btn-sm p-0 border-0"
                            title="View this golfer's offers page (opens a preview in a new tab)"
                            aria-label={`View offers as ${name || "this golfer"}`}
                            style={{ color: BRAND_GREEN, fontSize: "1rem", lineHeight: 1 }}
                            onClick={() => window.open(`/offers?previewContact=${encodeURIComponent(g.contactId)}&redeemed=1`, "_blank")}
                          >
                            <i className="bi bi-eye" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
